/**
 * PDF Highlight Overlay Component
 *
 * Renders yellow highlight rectangles over PDF content for citations.
 * Uses pdf.js viewport helpers to correctly position highlights
 * regardless of zoom level, rotation, or crop boxes.
 */

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import type { Rect } from '@/stores/preview-store'
import { isRunningBandBox } from '@/lib/citation-page-resolution'

interface Props {
  rects: Rect[]
  viewport: {
    convertToViewportRectangle: (rect: number[]) => number[]
  }
  highlightId: string
  isActive?: boolean
  /**
   * Rendered page height in viewport units. When supplied, rects lying entirely
   * inside the running header/footer bands are dropped (see isRunningBandBox).
   */
  pageHeight?: number
}

/**
 * Renders highlight overlays for a set of rectangles on a PDF page.
 *
 * The viewport.convertToViewportRectangle helper handles:
 * - Scale transformation (zoom levels)
 * - Rotation (landscape pages)
 * - Crop box offsets
 *
 * @param rects - Array of rectangles in PDF user space coordinates
 * @param viewport - PDF.js viewport object with conversion helper
 * @param highlightId - Unique identifier for this highlight group
 * @param isActive - Whether this highlight should be emphasized (pulsing animation)
 */
export function PdfHighlightOverlay({
  rects,
  viewport,
  highlightId,
  isActive = true,
  pageHeight,
}: Props) {
  const viewportRects = useMemo(() => {
    const boxes = rects.map((rect) => {
      // Use pdf.js viewport helper — handles rotation, crop boxes, etc.
      const [x1, y1, x2, y2] = viewport.convertToViewportRectangle([
        rect.x1,
        rect.y1,
        rect.x2,
        rect.y2,
      ])

      // convertToViewportRectangle may return coords in any order
      // (depends on rotation), so normalize to left/top/width/height
      // Skip if any coordinate is undefined
      if (x1 == null || y1 == null || x2 == null || y2 == null) {
        return null
      }

      return {
        left: Math.min(x1, x2),
        top: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1),
      }
    })

    const drawable = boxes.filter((box): box is NonNullable<typeof box> => box != null)
    if (pageHeight == null) return drawable

    const body = drawable.filter((box) => !isRunningBandBox(box, pageHeight))

    // Suppress boilerplate only when there is real content to show instead.
    // If every rect landed in a running band the data is all we have, and a
    // page with zero highlights reads as "the citation is fabricated" — worse
    // than a badly-placed one.
    return body.length > 0 ? body : drawable
  }, [rects, viewport, pageHeight])

  return (
    <>
      {viewportRects.map((box, i) => (
        <div
          key={`${highlightId}-${i}`}
          data-pdf-highlight-rect={highlightId}
          className={cn(
            'absolute pointer-events-none rounded-sm',
            // z-10 ensures overlay sits above text layer but below controls
            'z-10',
            isActive
              ? 'bg-yellow-300/40 animate-highlight-pulse'
              : 'bg-yellow-200/25'
          )}
          style={{
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height,
          }}
        />
      ))}
    </>
  )
}
